import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { toSceneSheet, type SceneSheet } from './types'
import type { CaseCandidate } from '../cases/types'

type Schema = components['schemas']

/**
 * Query keys.
 *
 * A scene is two cache entries for the same reason a campaign is: the API splits it.
 * `GET /scenes/{id}` serves core, aspects, characters and panels; the snapshot is its
 * own read because it is a multi-kilobyte blob and most scene opens never show it.
 * Mutations patch both, each filtered to what it holds (lib/sheetCache.ts).
 */
export const sceneKeys = {
  all: ['scenes'] as const,
  sheets: () => [...sceneKeys.all, 'sheet'] as const,
  sheet: (sceneId: number) => [...sceneKeys.sheets(), sceneId] as const,
  snapshots: () => [...sceneKeys.all, 'snapshot'] as const,
  snapshot: (sceneId: number) => [...sceneKeys.snapshots(), sceneId] as const,
  candidates: (sceneId: number) => [...sceneKeys.all, sceneId, 'available-characters'] as const,
}

/** The scene sheet: core, aspects, cast and panels. Any campaign member may read it. */
export function useSceneSheet(sceneId: number): UseQueryResult<SceneSheet> {
  return useQuery({
    queryKey: sceneKeys.sheet(sceneId),
    enabled: Number.isFinite(sceneId),
    queryFn: async () => toSceneSheet(await api.get<Schema['SceneSheetDto']>(`/scenes/${sceneId}`)),
  })
}

/**
 * The character-state snapshot.
 *
 * Fetched only when something actually shows it, which is the reason it is a separate
 * endpoint at all — so this defaults to disabled and the snapshot card opts in.
 */
export function useSceneSnapshot(sceneId: number, enabled: boolean): UseQueryResult<SceneSheet> {
  return useQuery({
    queryKey: sceneKeys.snapshot(sceneId),
    enabled: enabled && Number.isFinite(sceneId),
    queryFn: async () =>
      toSceneSheet(await api.get<Schema['SceneSheetDto']>(`/scenes/${sceneId}/snapshot`)),
  })
}

/**
 * Campaign-roster characters not yet in the scene. Server-filtered, so the picker never
 * offers somebody the API would refuse.
 */
export function useSceneCandidates(
  sceneId: number,
  enabled = true,
): UseQueryResult<CaseCandidate[]> {
  return useQuery({
    queryKey: sceneKeys.candidates(sceneId),
    enabled: enabled && Number.isFinite(sceneId),
    queryFn: () => api.get<CaseCandidate[]>(`/scenes/${sceneId}/available-characters`),
  })
}
